import React, { useState, useEffect } from 'react';
import {
  getDailyReportAPI,
  getTokenWiseReportAPI,
  getINRWiseReportAPI,
  getDollarWiseReportAPI,
  getSaleReportAPI,
  getPurchaseReportAPI,
  getProfitLossReportAPI,
  getSettlementReportAPI,
  getTransactionHistoryReportAPI,
  getTokenListAPI,
  getInternalAccountListAPI,
  getDollarAccountListAPI,
  getPartyListAPI,
  TokenWiseReportResponse,
  INRWiseReportResponse,
  DollarWiseReportResponse,
  ProfitLossReportResponse,
  SettlementReportResponse,
  TransactionHistoryReportResponse,
  Token,
  InternalAccount,
  DollarAccount,
  Party,
} from '../../../services/adminApi';
import BalanceDetailModal from './BalanceDetailModal';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './Reports.module.css';

interface ReportTab {
  id: string;
  label: string;
}

interface DailyReportData {
  totalBuyAmt: number;
  totalSellAmt: number;
  totalGrossProfit: number;
  totalTransaction: number;
  totalBuyTransaction: number;
  totalSellTransaction: number;
  totalSwapTransaction: number;
}

interface TokenWiseData {
  tokenName: string;
  symbol: string;
  buyQty: number;
  buyAmt: number;
  sellQty: number;
  sellAmt: number;
  netQty: number;
}

interface INRWiseData {
   accountName:string;
  accountType:string;
  inAmt: number;
  outAmt: number;
  netAmt: number;
}

interface DollarWiseData {
  accountName:string;
  accountType:string;
  inAmt: number;
  outAmt: number;
  netAmt: number;
}

interface SaleReportData {
  totalSales: number;
  totalQtySold: number;
  transactionCount: number;
}

interface SaleReportItem {
  id?: number;
  sellDate?: string;
  partyId?: number;
  partyName?: string;
  sellQty?: number;
  totalAmtInINR?: number;
}

interface PurchaseReportData {
  totalPurchase: number;
  totalQtyBought: number;
  transactionCount: number;
}

interface PurchaseReportItem {
  id?: number;
  buyDate?: string;
  partyId?: number;
  partyName?: string;
  buyQty?: number;
  totalAmtInINR?: number;
}

const Reports: React.FC = () => {
  const [activeTab, setActiveTab] = useState('daily');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedParty, setSelectedParty] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  
  // Get permissions for Reports module
  const permissions = useModulePermission('Reports');

  // Balance Detail Modal State
  const [showBalanceDetail, setShowBalanceDetail] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{
    refId: number;
    refType: string;
    title: string;
    displayName: string;
  } | null>(null);

  // Daily Report Data
  const [dailyReportData, setDailyReportData] = useState<DailyReportData>({
    totalBuyAmt: 0,
    totalSellAmt: 0,
    totalGrossProfit: 0,
    totalTransaction: 0,
    totalBuyTransaction: 0,
    totalSellTransaction: 0,
    totalSwapTransaction: 0,
  });
  const [tokenWiseData, setTokenWiseData] = useState<TokenWiseData[]>([]);
  const [inrWiseData, setINRWiseData] = useState<INRWiseData[]>([]);
  const [dollarWiseData, setDollarWiseData] = useState<DollarWiseData[]>([]);
  
  // Sale and Purchase Report Data
  const [saleReportData, setSaleReportData] = useState<SaleReportData>({
    totalSales: 0,
    totalQtySold: 0,
    transactionCount: 0,
  });
  const [saleReportItems, setSaleReportItems] = useState<SaleReportItem[]>([]);
  
  const [purchaseReportData, setPurchaseReportData] = useState<PurchaseReportData>({
    totalPurchase: 0,
    totalQtyBought: 0,
    transactionCount: 0,
  });
  const [purchaseReportItems, setPurchaseReportItems] = useState<PurchaseReportItem[]>([]);

  // P&L Tab Data
  const [pnlItems, setPnlItems] = useState<ProfitLossReportResponse[]>([]);

  // Balance Tab Data
  const [tokenBalances, setTokenBalances] = useState<Token[]>([]);
  const [internalAccounts, setInternalAccounts] = useState<InternalAccount[]>([]);
  const [dollarAccounts, setDollarAccounts] = useState<DollarAccount[]>([]);

  // Settlement Tab Data
  const [settlementItems, setSettlementItems] = useState<SettlementReportResponse[]>([]);

  // Transaction History Tab Data
  const [transactionHistoryItems, setTransactionHistoryItems] = useState<TransactionHistoryReportResponse[]>([]);

  // Party List Data
  const [partyList, setPartyList] = useState<Party[]>([]);

  const reportTabs: ReportTab[] = [
    { id: 'daily', label: 'Daily' },
    { id: 'settlement', label: 'Settlement' },
    { id: 'balance', label: 'Balance' },
    { id: 'pnl', label: 'P&L' },
    { id: 'sale', label: 'Sale' },
    { id: 'purchase', label: 'Purchase' },
    { id: 'txn-history', label: 'Txn History' },
  ];

  // Fetch party list on component mount
  useEffect(() => {
    fetchPartyList();
  }, []);

  const fetchPartyList = async () => {
    try {
      const response = await getPartyListAPI({ pageNo: 1, pageSize: 1000 });
      if (response.isSuccess && response.data && Array.isArray(response.data)) {
        setPartyList(response.data);
      }
    } catch (err) {
      console.error('Party list fetch error:', err);
    }
  };

  // Fetch Daily Report data when dates change
  useEffect(() => {
    if (activeTab === 'daily') {
      fetchDailyReportData();
    } else if (activeTab === 'sale') {
      fetchSaleReportData();
    } else if (activeTab === 'purchase') {
      fetchPurchaseReportData();
    } else if (activeTab === 'pnl') {
      fetchProfitLossData();
    } else if (activeTab === 'balance') {
      fetchBalanceData();
    } else if (activeTab === 'settlement') {
      fetchSettlementData();
    } else if (activeTab === 'txn-history') {
      fetchBalanceData();
      fetchTransactionHistoryData();
    }
  }, [fromDate, toDate, activeTab, selectedParty, selectedAccount]);

  const fetchDailyReportData = async () => {
    try {
      const [dailyResponse, tokenResponse, inrResponse, dollarResponse] = await Promise.all([
        getDailyReportAPI({ startDate: fromDate, endDate: toDate, pageNo: 1, pageSize: 10 }),
        getTokenWiseReportAPI({ startDate: fromDate, endDate: toDate, pageNo: 1, pageSize: 100 }),
        getINRWiseReportAPI({ startDate: fromDate, endDate: toDate, pageNo: 1, pageSize: 10 }),
        getDollarWiseReportAPI({ startDate: fromDate, endDate: toDate, pageNo: 1, pageSize: 10 }),
      ]);

      // Handle Daily Report
      if (dailyResponse.isSuccess && dailyResponse.data) {
        setDailyReportData({
          totalBuyAmt: dailyResponse.data.totalBuyAmt || 0,
          totalSellAmt: dailyResponse.data.totalSellAmt || 0,
          totalGrossProfit: dailyResponse.data.totalGrossProfit || 0,
          totalTransaction: dailyResponse.data.totalTransaction || 0,
          totalBuyTransaction: dailyResponse.data.totalBuyTransaction || 0,
          totalSellTransaction: dailyResponse.data.totalSellTransaction || 0,
          totalSwapTransaction: dailyResponse.data.totalSwapTransaction || 0,
        });
      }

      // Handle Token Wise Report
      if (tokenResponse.isSuccess && tokenResponse.data && Array.isArray(tokenResponse.data)) {
        const tokens = tokenResponse.data.map((token: TokenWiseReportResponse) => ({
          tokenName: token.tokenName || '',
          symbol: token.symbol || '',
          buyQty: token.buyQty || 0,
          buyAmt: token.buyAmt || 0,
          sellQty: token.sellQty || 0,
          sellAmt: token.sellAmt || 0,
          netQty: token.netQty || 0,
        }));
        setTokenWiseData(tokens);
      }

      // Handle INR Wise Report
      if (inrResponse.isSuccess && inrResponse.data && Array.isArray(inrResponse.data)) {
        const inrs = inrResponse.data.map((inr: INRWiseReportResponse) => ({
          accountName: inr.accountName,
          accountType: inr.accountType,
          inAmt: inr.inAmt || 0,
          outAmt: inr.outAmt || 0,
          netAmt: inr.netAmt || 0,
        }));
        setINRWiseData(inrs);
      }

      // Handle Dollar Wise Report
      if (dollarResponse.isSuccess && dollarResponse.data && Array.isArray(dollarResponse.data)) {
        const dollars = dollarResponse.data.map((dollar: DollarWiseReportResponse) => ({
          accountName: dollar.accountName,
          accountType: dollar.accountType,
          inAmt: dollar.inAmt || 0,
          outAmt: dollar.outAmt || 0,
          netAmt: dollar.netAmt || 0,
        }));
        setDollarWiseData(dollars);
      }
     
    } catch (err) {
      console.error('Daily report fetch error:', err);
    }
  };

  const fetchSaleReportData = async () => {
    try {
      // Build request object matching business API payload structure exactly
      const request: any = {
        fromDate: null,
        toDate: null,
        partyId: 0,
        tokenId: 0,
        searchText: '',
        isActive: null,
        pageNo: 1,
        pageSize: 100,
      };

      // Add date filters - convert to ISO 8601 DateTime format
      if (fromDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T00:00:00"
        request.fromDate = `${fromDate}T00:00:00`;
      }
      if (toDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T23:59:59"
        request.toDate = `${toDate}T23:59:59`;
      }

      // Add optional filters
      if (selectedParty !== '') {
        request.partyId = parseInt(selectedParty);
      }

      const [saleResponse] = await Promise.all([
        getSaleReportAPI(request),
      ]);

      if (saleResponse.isSuccess && saleResponse.data && Array.isArray(saleResponse.data)) {
        setSaleReportItems(saleResponse.data);

        // Calculate summary data from saleReportItems
        const totalSales = saleResponse.data.reduce((sum: number, item: any) => sum + (item.totalAmtInINR || 0), 0);
        const totalQtySold = saleResponse.data.reduce((sum: number, item: any) => sum + (item.sellQty || 0), 0);
        const transactionCount = saleResponse.data.length;

        setSaleReportData({
          totalSales: totalSales,
          totalQtySold: totalQtySold,
          transactionCount: transactionCount,
        });
      }
    } catch (err) {
      console.error('Sale report fetch error:', err);
    }
  };

  const fetchPurchaseReportData = async () => {
    try {
      // Build request object matching business API payload structure exactly
      const request: any = {
        fromDate: null,
        toDate: null,
        partyId: 0,
        tokenId: 0,
        searchText: '',
        isActive: null,
        pageNo: 1,
        pageSize: 100,
      };

      // Add date filters - convert to ISO 8601 DateTime format
      if (fromDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T00:00:00"
        request.fromDate = `${fromDate}T00:00:00`;
      }
      if (toDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T23:59:59"
        request.toDate = `${toDate}T23:59:59`;
      }

      // Add optional filters
      if (selectedParty !== '') {
        request.partyId = parseInt(selectedParty);
      }

      const [purchaseResponse] = await Promise.all([
        getPurchaseReportAPI(request),
      ]);

      if (purchaseResponse.isSuccess && purchaseResponse.data && Array.isArray(purchaseResponse.data)) {
        setPurchaseReportItems(purchaseResponse.data);

        // Calculate summary data from purchaseReportItems
        const totalPurchase = purchaseResponse.data.reduce((sum: number, item: any) => sum + (item.totalAmtInINR || 0), 0);
        const totalQtyBought = purchaseResponse.data.reduce((sum: number, item: any) => sum + (item.buyQty || 0), 0);
        const transactionCount = purchaseResponse.data.length;

        setPurchaseReportData({
          totalPurchase: totalPurchase,
          totalQtyBought: totalQtyBought,
          transactionCount: transactionCount,
        });
      }
    } catch (err) {
      console.error('Purchase report fetch error:', err);
    }
  };

  const fetchBalanceData = async () => {
    try {
      const [tokenResponse, internalResponse, dollarResponse] = await Promise.all([
        getTokenListAPI({ pageNo: 1, pageSize: 100 }),
        getInternalAccountListAPI({ pageNo: 1, pageSize: 100 }),
        getDollarAccountListAPI({ pageNo: 1, pageSize: 100 }),
      ]);

      if (tokenResponse.isSuccess && tokenResponse.data && Array.isArray(tokenResponse.data)) {
        setTokenBalances(tokenResponse.data);
      }

      if (internalResponse.isSuccess && internalResponse.data && Array.isArray(internalResponse.data)) {
        setInternalAccounts(internalResponse.data);
      }

      if (dollarResponse.isSuccess && dollarResponse.data && Array.isArray(dollarResponse.data)) {
        setDollarAccounts(dollarResponse.data);
      }
    } catch (err) {
      console.error('Balance data fetch error:', err);
    }
  };

  const fetchSettlementData = async () => {
    try {
      // Build request object matching business API payload structure exactly
      const request: any = {
        fromDate: null,
        toDate: null,
        partyId: 0,
        searchText: '',
        isActive: null,
        pageNo: 1,
        pageSize: 100,
      };

      // Add date filters - convert to ISO 8601 DateTime format
      if (fromDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T00:00:00"
        request.fromDate = `${fromDate}T00:00:00`;
      }
      if (toDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T23:59:59"
        request.toDate = `${toDate}T23:59:59`;
      }

      // Add optional filters
      if (selectedParty !== '') {
        request.partyId = parseInt(selectedParty);
      }

      const settlementResponse = await getSettlementReportAPI(request);

      if (settlementResponse.isSuccess && settlementResponse.data && Array.isArray(settlementResponse.data)) {
        setSettlementItems(settlementResponse.data);
      }
    } catch (err) {
      console.error('Settlement report fetch error:', err);
    }
  };

  const fetchProfitLossData = async () => {
    try {
      // Build request object matching business API payload structure exactly
      const request: any = {
        fromDate: null,
        toDate: null,
        searchText: '',
        isActive: null,
        pageNo: 1,
        pageSize: 100,
      };

      // Add date filters - convert to ISO 8601 DateTime format
      if (fromDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T00:00:00"
        request.fromDate = `${fromDate}T00:00:00`;
      }
      if (toDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T23:59:59"
        request.toDate = `${toDate}T23:59:59`;
      }

      const pnlResponse = await getProfitLossReportAPI(request);

      if (pnlResponse.isSuccess && pnlResponse.data && Array.isArray(pnlResponse.data)) {
        setPnlItems(pnlResponse.data);
      }
    } catch (err) {
      console.error('Profit Loss report fetch error:', err);
    }
  };

  const fetchTransactionHistoryData = async () => {
    try {
      // Build request object matching business API payload structure exactly
      const request: any = {
        fromDate: null,
        toDate: null,
        partyId: 0,
        accountId: 0,
        transactionTypeId: 0,
        searchText: '',
        isActive: null,
        pageNo: 1,
        pageSize: 100,
      };

      // Add date filters - convert to ISO 8601 DateTime format
      if (fromDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T00:00:00"
        request.fromDate = `${fromDate}T00:00:00`;
      }
      if (toDate) {
        // Convert date string "2026-05-07" to ISO DateTime "2026-05-07T23:59:59"
        request.toDate = `${toDate}T23:59:59`;
      }

      // Add optional filters
      if (selectedParty !== '') {
        request.partyId = parseInt(selectedParty);
      }

      if (selectedAccount !== '') {
        request.accountId = parseInt(selectedAccount);
      }

      const txnHistoryResponse = await getTransactionHistoryReportAPI(request);

      if (txnHistoryResponse.isSuccess && txnHistoryResponse.data && Array.isArray(txnHistoryResponse.data)) {
        setTransactionHistoryItems(txnHistoryResponse.data);
      }
    } catch (err) {
      console.error('Transaction history report fetch error:', err);
    }
  };

  const handleTokenBalanceClick = (token: Token) => {
    setSelectedItem({
      refId: token.id || 0,
      refType: 'Token',
      title: 'Balance Detail',
      displayName: `${token.tokenName} (${token.symbol})`,
    });
    setShowBalanceDetail(true);
  };

  const handleInternalAccountClick = (account: InternalAccount) => {
    setSelectedItem({
      refId: account.id || 0,
      refType: 'INR',
      title: 'Balance Detail',
      displayName: account.accountName,
    });
    setShowBalanceDetail(true);
  };

  const handleDollarAccountClick = (account: DollarAccount) => {
    setSelectedItem({
      refId: account.id || 0,
      refType: 'Dollar',
      title: 'Balance Detail',
      displayName: account.accountName,
    });
    setShowBalanceDetail(true);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Reports</h1>
          <p className={styles.subtitle}>Analytics & financial summaries</p>
        </div>
      </div>

      {/* Report Tabs */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          {reportTabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
            >
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Date Range Filter - Hide for Balance, Sale, Purchase, and Txn History tabs */}
      {activeTab !== 'balance' && activeTab !== 'sale' && activeTab !== 'purchase' && activeTab !== 'txn-history' && (
        <div className={styles.filterSection}>
          <div className={styles.dateRangeContainer}>
            <div className={styles.dateGroup}>
              <label className={styles.dateLabel}>Start Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            <div className={styles.dateGroup}>
              <label className={styles.dateLabel}>End Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            {activeTab === 'settlement' && (
              <div className={styles.dateGroup}>
                <label className={styles.dateLabel}>Party</label>
                <select
                  value={selectedParty}
                  onChange={(e) => setSelectedParty(e.target.value)}
                  className={styles.partySelect}
                >
                  <option value="">All Parties</option>
                  {partyList.map((party) => (
                    <option key={party.id} value={party.id || ''}>
                      {party.partyName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards - Only show for Daily tab */}
      {activeTab === 'daily' && (
        <div className={styles.summarySection}>
          <div className={styles.summaryCards}>
            <div className={`${styles.card} ${styles.cardGreen}`}>
              <div className={styles.cardLabel}>TOTAL BUY</div>
              <div className={styles.cardValue}>₹{dailyReportData.totalBuyAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
              <div className={styles.cardSubtext}>{dailyReportData.totalBuyTransaction || 0} txns</div>
            </div>
            <div className={`${styles.card} ${styles.cardRed}`}>
              <div className={styles.cardLabel}>TOTAL SELL</div>
              <div className={styles.cardValue}>₹{dailyReportData.totalSellAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
              <div className={styles.cardSubtext}>{dailyReportData.totalSellTransaction || 0} txn</div>
            </div>
            <div className={`${styles.card} ${styles.cardBlue}`}>
              <div className={styles.cardLabel}>GROSS PROFIT</div>
              <div className={styles.cardValue}>₹{dailyReportData.totalGrossProfit?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
            </div>
            <div className={`${styles.card} ${styles.cardGray}`}>
              <div className={styles.cardLabel}>TOTAL TXNS</div>
              <div className={styles.cardValue}>{dailyReportData.totalTransaction || 0}</div>
              <div className={styles.cardSubtext}>{dailyReportData.totalSwapTransaction || 0} swaps</div>
            </div>
          </div>
          <div className={styles.periodInfo}>Period: {fromDate} to {toDate}</div>
        </div>
      )}

      {/* Settlement Tab Content */}
      {activeTab === 'settlement' && (
        <div className={styles.settlementContent}>
          {permissions.canExport && (
            <div className={styles.exportButtonContainer}>
              <button className={styles.exportBtn}>📥 Export</button>
            </div>
          )}

          {/* Settlement Items */}
          {settlementItems && settlementItems.length > 0 ? (
            settlementItems.map((item, index) => (
              <div key={index} className={styles.settlementCard}>
                <div className={styles.partyNameHeader}>
                  {item.partyName || 'N/A'}
                  <span className={`${styles.receivableBadge} ${item.status === 'Payable' ? styles.payableBadge : (item.status === 'pending' || item.status === 'Pending') ? styles.pendingBadge : ''}`}>
                    {item.status || 'pending'}
                  </span>
                </div>
                <div className={styles.settlementItemsRow}>
                  <div className={styles.settlementItemColumn}>
                    <div className={styles.settlementItemLabel}><span className={item.transactionType === 'Sell' ? styles.textRed : styles.textGreen}>{item.transactionType}</span> {item.tokenName || 'Token'} - {item.symbol || ''}</div>
                    <div className={`${styles.settlementItemValue} ${styles.textGreen}`}>
                      {item.qty?.toFixed(4) || '0.0000'} 
                    </div>
                  </div>
                  <div className={styles.settlementItemColumn}>
                    <div className={styles.settlementItemLabel}>'Payment' - {item.paymentIn || ''}</div>
                    <div className={`${styles.settlementItemValue} ${styles.textRed}`}>
                      ₹{item.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                    </div>
                  </div>
                  <div className={styles.settlementItemColumn}>
                    <div className={styles.settlementItemLabel}>{item.transactionType === 'Sell' ? 'Received In' : 'Pay To'}</div>
                    <div className={`${styles.settlementItemValue} ${styles.textBlue}`}>
                      {item.payToAccountNo || ''}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.settlementCard}>
              <div className={styles.partyNameHeader}>No settlement data available</div>
            </div>
          )}
        </div>
      )}

      {/* Balance Tab Content */}
      {activeTab === 'balance' && (
        <div className={styles.balanceContent}>
          {permissions.canExport && (
            <div className={styles.exportButtonContainer}>
              <button className={styles.exportBtn}>📥 Export</button>
            </div>
          )}

          {/* Token Balances Section */}
          <div className={styles.balanceSection}>
            <div className={styles.balanceSectionHeader}>
              <span className={styles.balanceSectionIcon}>🪙</span>
              <h3 className={styles.balanceSectionTitle}>Token Balances</h3>
            </div>
            <div className={styles.balanceItemsList}>
              {tokenBalances && tokenBalances.length > 0 ? (
                tokenBalances.map((token) => (
                  <div
                    key={token.id}
                    className={styles.balanceItem}
                    onClick={() => handleTokenBalanceClick(token)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.balanceItemLeft}>
                      <div className={styles.balanceItemName}>{token.tokenName}</div>
                      <div className={styles.balanceItemCode}>{token.symbol}</div>
                    </div>
                    <div className={styles.balanceItemRight}>
                      <div className={styles.balanceItemValue}>{parseFloat(token.currentBal || '0').toFixed(6)}</div>
                      <span className={styles.balanceItemArrow}>→</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.balanceItem}>
                  <div className={styles.balanceItemLeft}>
                    <div className={styles.balanceItemName}>No tokens available</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Internal Accounts Section */}
          <div className={styles.balanceSection}>
            <div className={styles.balanceSectionHeader}>
              <span className={styles.balanceSectionIcon}>🏦</span>
              <h3 className={styles.balanceSectionTitle}>Internal Accounts</h3>
            </div>
            <div className={styles.balanceItemsList}>
              {internalAccounts && internalAccounts.length > 0 ? (
                internalAccounts.map((account) => (
                  <div
                    key={account.id}
                    className={styles.balanceItem}
                    onClick={() => handleInternalAccountClick(account)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.balanceItemLeft}>
                      <div className={styles.balanceItemName}>{account.accountName}</div>
                      <div className={styles.balanceItemCode}>{account.accountType}</div>
                    </div>
                    <div className={styles.balanceItemRight}>
                      <div className={styles.balanceItemValue}>₹{account.currentBal?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                      <span className={styles.balanceItemArrow}>→</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.balanceItem}>
                  <div className={styles.balanceItemLeft}>
                    <div className={styles.balanceItemName}>No internal accounts available</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dollar Accounts Section */}
          <div className={styles.balanceSection}>
            <div className={styles.balanceSectionHeader}>
              <span className={styles.balanceSectionIcon}>💵</span>
              <h3 className={styles.balanceSectionTitle}>Dollar Accounts</h3>
            </div>
            <div className={styles.balanceItemsList}>
              {dollarAccounts && dollarAccounts.length > 0 ? (
                dollarAccounts.map((account) => (
                  <div
                    key={account.id}
                    className={styles.balanceItem}
                    onClick={() => handleDollarAccountClick(account)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.balanceItemLeft}>
                      <div className={styles.balanceItemName}>{account.accountName}</div>
                      <div className={styles.balanceItemCode}>{account.accountType}</div>
                    </div>
                    <div className={styles.balanceItemRight}>
                      <div className={styles.balanceItemValue}>${account.currentBal?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                      <span className={styles.balanceItemArrow}>→</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.balanceItem}>
                  <div className={styles.balanceItemLeft}>
                    <div className={styles.balanceItemName}>No dollar accounts available</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* P&L Tab Content */}
      {activeTab === 'pnl' && (
        <div className={styles.pnlContent}>
          <div className={styles.pnlHeaderSection}>
            <div className={styles.pnlNetProfitBox}>
              <div className={styles.pnlNetProfitLabel}>NET PROFIT / LOSS</div>
              <div className={`${styles.pnlNetProfitValue} ${pnlItems && pnlItems.length > 0 && pnlItems.reduce((sum: number, item: any) => sum + (item.netAmt || 0), 0) >= 0 ? styles.textGreen : styles.textRed}`}>
                ₹{pnlItems && pnlItems.length > 0 ? pnlItems.reduce((sum: number, item: any) => sum + (item.netAmt || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
              </div>
            </div>
            {permissions.canExport && (
              <button className={styles.exportBtn}>📥 Export</button>
            )}
          </div>

          {/* P&L Items */}
          <div className={styles.pnlItemsContainer}>
            {pnlItems && pnlItems.length > 0 ? (
              pnlItems.map((item, index) => (
                <div key={index} className={styles.pnlItemRow}>
                  <div className={styles.pnlItemRowLeft}>
                    <div className={styles.pnlItemRowName}>{item.tokenName} <span className={styles.pnlItemRowCode}>{item.symbol}</span></div>
                    <div className={styles.pnlItemRowBuy}>Buy: {item.buyQty?.toFixed(4) || '0.0000'}<br/>₹{item.buyAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                  </div>
                  <div className={styles.pnlItemRowMiddle}>
                    <div className={styles.pnlItemRowSell}>Sell: {item.sellQty?.toFixed(4) || '0.0000'}<br/>₹{item.sellAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                  </div>
                  <div className={`${styles.pnlItemRowRight} ${(item.netAmt || 0) >= 0 ? styles.textGreen : styles.textRed}`}>₹{item.netAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                </div>
              ))
            ) : (
              <div className={styles.pnlItemRow}>
                <div className={styles.pnlItemRowLeft}>
                  <div className={styles.pnlItemRowName}>No P&L data available</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sale Tab Content */}
      {activeTab === 'sale' && (
        <div className={styles.saleContent}>
          <div className={styles.saleFilterSection}>
            <div className={styles.saleFilterGroup}>
              <label className={styles.dateLabel}>Start Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            <div className={styles.saleFilterGroup}>
              <label className={styles.dateLabel}>End Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            <div className={styles.saleFilterGroup}>
              <label className={styles.dateLabel}>Party</label>
              <select
                value={selectedParty}
                onChange={(e) => setSelectedParty(e.target.value)}
                className={styles.partySelect}
              >
                <option value="">All Parties</option>
                {partyList.map((party) => (
                  <option key={party.id} value={party.id || ''}>
                    {party.partyName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.saleFilterGroup}>
              <label className={styles.dateLabel}>Token</label>
              <select className={styles.partySelect}>
                <option>All Tokens</option>
                <option>BTC</option>
                <option>ETH</option>
                <option>USDT</option>
              </select>
            </div>
          </div>

          {/* Summary Cards */}
          <div className={styles.saleSummaryCards}>
            <div className={`${styles.saleSummaryCard} ${styles.saleSummaryCardRed}`}>
              <div className={styles.saleSummaryLabel}>TOTAL SALES</div>
              <div className={`${styles.saleSummaryValue} ${styles.textRed}`}>₹{saleReportData.totalSales?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
            </div>
            <div className={`${styles.saleSummaryCard} ${styles.saleSummaryCardBlue}`}>
              <div className={styles.saleSummaryLabel}>QTY SOLD</div>
              <div className={styles.saleSummaryValue}>{saleReportData.totalQtySold?.toFixed(4) || '0.0000'}</div>
            </div>
            <div className={`${styles.saleSummaryCard} ${styles.saleSummaryCardGray}`}>
              <div className={styles.saleSummaryLabel}>COUNT</div>
              <div className={styles.saleSummaryValue}>{saleReportData.transactionCount || 0}</div>
            </div>
          </div>

          {/* Export Button */}
          {permissions.canExport && (
            <div className={styles.saleExportContainer}>
              <button className={styles.exportBtn}>📥 Export</button>
            </div>
          )}

          {/* Sale Items */}
          <div className={styles.saleItemsList}>
            {saleReportItems && saleReportItems.length > 0 ? (
              saleReportItems.map((item, index) => (
                <div key={index} className={styles.saleItemRow}>
                  <div className={styles.saleItemLeft}>
                    <div className={styles.saleItemName}>{item.partyName || 'N/A'}</div>
                    <div className={styles.saleItemDate}>
                      {item.sellDate ? new Date(item.sellDate).toLocaleDateString('en-IN') : 'N/A'} | Qty: {item.sellQty?.toFixed(4) || '0.0000'}
                    </div>
                  </div>
                  <div className={`${styles.saleItemRight} ${styles.textRed}`}>
                    ₹{item.totalAmtInINR?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.saleItemRow}>
                <div className={styles.saleItemLeft}>
                  <div className={styles.saleItemName}>No data available</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Purchase Tab Content */}
      {activeTab === 'purchase' && (
        <div className={styles.purchaseContent}>
          <div className={styles.purchaseFilterSection}>
            <div className={styles.purchaseFilterGroup}>
              <label className={styles.dateLabel}>Start Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            <div className={styles.purchaseFilterGroup}>
              <label className={styles.dateLabel}>End Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            <div className={styles.purchaseFilterGroup}>
              <label className={styles.dateLabel}>Party</label>
              <select
                value={selectedParty}
                onChange={(e) => setSelectedParty(e.target.value)}
                className={styles.partySelect}
              >
                <option value="">All Parties</option>
                {partyList.map((party) => (
                  <option key={party.id} value={party.id || ''}>
                    {party.partyName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.purchaseFilterGroup}>
              <label className={styles.dateLabel}>Token</label>
              <select className={styles.partySelect}>
                <option>All Tokens</option>
                <option>BTC</option>
                <option>ETH</option>
                <option>USDT</option>
              </select>
            </div>
          </div>

          {/* Summary Cards */}
          <div className={styles.purchaseSummaryCards}>
            <div className={`${styles.purchaseSummaryCard} ${styles.purchaseSummaryCardGreen}`}>
              <div className={styles.purchaseSummaryLabel}>TOTAL PURCHASE</div>
              <div className={`${styles.purchaseSummaryValue} ${styles.textGreen}`}>₹{purchaseReportData.totalPurchase?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
            </div>
            <div className={`${styles.purchaseSummaryCard} ${styles.purchaseSummaryCardBlue}`}>
              <div className={styles.purchaseSummaryLabel}>QTY BOUGHT</div>
              <div className={styles.purchaseSummaryValue}>{purchaseReportData.totalQtyBought?.toFixed(4) || '0.0000'}</div>
            </div>
            <div className={`${styles.purchaseSummaryCard} ${styles.purchaseSummaryCardGray}`}>
              <div className={styles.purchaseSummaryLabel}>COUNT</div>
              <div className={styles.purchaseSummaryValue}>{purchaseReportData.transactionCount || 0}</div>
            </div>
          </div>

          {/* Export Button */}
          {permissions.canExport && (
            <div className={styles.purchaseExportContainer}>
              <button className={styles.exportBtn}>📥 Export</button>
            </div>
          )}

          {/* Purchase Items */}
          <div className={styles.purchaseItemsList}>
            {purchaseReportItems && purchaseReportItems.length > 0 ? (
              purchaseReportItems.map((item, index) => (
                <div key={index} className={styles.purchaseItemRow}>
                  <div className={styles.purchaseItemLeft}>
                    <div className={styles.purchaseItemName}>{item.partyName || 'N/A'}</div>
                    <div className={styles.purchaseItemDate}>
                      {item.buyDate ? new Date(item.buyDate).toLocaleDateString('en-IN') : 'N/A'} | Qty: {item.buyQty?.toFixed(4) || '0.0000'}
                    </div>
                  </div>
                  <div className={`${styles.purchaseItemRight} ${styles.textGreen}`}>
                    ₹{item.totalAmtInINR?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.purchaseItemRow}>
                <div className={styles.purchaseItemLeft}>
                  <div className={styles.purchaseItemName}>No data available</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Txn History Tab Content */}
      {activeTab === 'txn-history' && (
        <div className={styles.txnHistoryContent}>
          <div className={styles.txnHistoryFilterSection}>
            <div className={styles.txnHistoryFilterGroup}>
              <label className={styles.dateLabel}>Start Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            <div className={styles.txnHistoryFilterGroup}>
              <label className={styles.dateLabel}>End Date</label>
              <div className={styles.dateInputWrapper}>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={styles.dateInput}
                />
                <span className={styles.calendarIcon}>📅</span>
              </div>
            </div>
            <div className={styles.txnHistoryFilterGroup}>
              <label className={styles.dateLabel}>Party</label>
              <select
                value={selectedParty}
                onChange={(e) => setSelectedParty(e.target.value)}
                className={styles.partySelect}
              >
                <option value="">All Parties</option>
                {partyList.map((party) => (
                  <option key={party.id} value={party.id || ''}>
                    {party.partyName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.txnHistoryFilterGroup}>
              <label className={styles.dateLabel}>Account</label>
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className={styles.partySelect}
              >
                <option value="">All Accounts</option>
                {internalAccounts.map((account) => (
                  <option key={account.id} value={account.id || ''}>
                    {account.accountName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.txnHistoryFilterGroup}>
              <label className={styles.dateLabel}>Type</label>
              <select className={styles.partySelect}>
                <option>All</option>
                <option>Buy</option>
                <option>Sell</option>
                <option>Swap</option>
              </select>
            </div>
          </div>

          {/* Export Button */}
          {permissions.canExport && (
            <div className={styles.txnHistoryExportContainer}>
              <button className={styles.exportBtn}>📥 Export</button>
            </div>
          )}

          {/* Transaction Items */}
          <div className={styles.txnHistoryItemsList}>
            {transactionHistoryItems && transactionHistoryItems.length > 0 ? (
              transactionHistoryItems.map((item, index) => (
                <div key={index} className={styles.txnHistoryItemRow}>
                  <div className={styles.txnHistoryItemLeft}>
                    <span className={`${styles.txnHistoryBadge} ${item.transactionType === 'Sell' ? styles.txnHistoryBadgeSell : item.transactionType === 'Buy' ? styles.txnHistoryBadgeBuy : styles.txnHistoryBadgeSwap}`}>
                      {item.transactionType || 'N/A'}
                    </span>
                    <div className={styles.txnHistoryItemInfo}>
                      <div className={styles.txnHistoryItemDate}>
                        {item.transactionDate ? new Date(item.transactionDate).toLocaleDateString('en-IN') : 'N/A'} {item.symbol}
                      </div>
                      <div className={styles.txnHistoryItemDesc}>
                        {item.partyName || item.accountName || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className={styles.txnHistoryItemRight}>
                    {item.transactionType === 'Buy' ? (
                      <>
                        <div className={styles.txnHistoryAmountBlock}>
                          <span className={styles.textGreen}>+₹{item.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
                          <div className={styles.txnHistoryPaymentType}>{item.paymentIn1}</div>
                        </div>
                        <div className={styles.txnHistoryAmountBlock}>
                          <span className={styles.textRed}>-{(item.Qty || (item as any).qty)?.toFixed(4) || '0.0000'}</span>
                          <div className={styles.txnHistoryPaymentType}>{item.paymentIn2}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.txnHistoryAmountBlock}>
                          <span className={styles.textRed}>-{(item.Qty || (item as any).qty)?.toFixed(4) || '0.0000'}</span>
                          <div className={styles.txnHistoryPaymentType}>{item.paymentIn1}</div>
                        </div>
                        <div className={styles.txnHistoryAmountBlock}>
                          <span className={styles.textGreen}>+₹{item.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
                          <div className={styles.txnHistoryPaymentType}>{item.paymentIn2}</div>
                        </div>
                      </>
                    )}
                    <div className={styles.txnHistoryBalanceContainer}>
                      <div className={styles.txnHistoryBalanceHeader}>BALANCE</div>
                      <div className={styles.txnHistoryBalanceContent}>
                        <div className={styles.txnHistoryBalanceColumn}>
                          <div className={styles.txnHistoryBalanceType}>INR</div>
                          <div className={styles.txnHistoryBalanceValue}>₹{item.inrBal?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                        </div>
                        <div className={styles.txnHistoryBalanceColumn}>
                          <div className={styles.txnHistoryBalanceType}>TOKEN</div>
                          <div className={styles.txnHistoryBalanceValue}>{item.tokenBal?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                        </div>
                        <div className={styles.txnHistoryBalanceColumn}>
                          <div className={styles.txnHistoryBalanceType}>DOLLAR</div>
                          <div className={styles.txnHistoryBalanceValue}>${item.dollarBal?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.txnHistoryItemRow}>
                <div className={styles.txnHistoryItemLeft}>
                  <div className={styles.txnHistoryItemInfo}>
                    <div className={styles.txnHistoryItemDesc}>No transaction history available</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily Tab Reports - Only show for Daily tab */}
      {activeTab === 'daily' && (
        <>
          <div className={styles.reportSection}>
            <div className={`${styles.reportHeader} ${styles.headerBlue}`}>
              <h3 className={styles.reportTitle}>📊 TOKEN-WISE REPORT</h3>
            </div>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thBlue}>TOKEN</th>
                    <th className={`${styles.alignRight} ${styles.thGreen}`}>BUY QTY</th>
                    <th className={`${styles.alignRight} ${styles.thGreen}`}>BUY AMOUNT</th>
                    <th className={`${styles.alignRight} ${styles.thRed}`}>SELL QTY</th>
                    <th className={`${styles.alignRight} ${styles.thRed}`}>SELL AMOUNT</th>
                    <th className={`${styles.alignRight} ${styles.thBlue}`}>NET QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {tokenWiseData && tokenWiseData.length > 0 ? (
                    tokenWiseData.map((token, index) => (
                      <tr key={index}>
                        <td>{token.tokenName} ({token.symbol})</td>
                        <td className={`${styles.alignRight} ${styles.textGreen}`}>{token.buyQty?.toFixed(4) || '0.0000'}</td>
                        <td className={`${styles.alignRight} ${styles.textGreen}`}>₹{token.buyAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                        <td className={`${styles.alignRight} ${styles.textRed}`}>{token.sellQty?.toFixed(4) || '0.0000'}</td>
                        <td className={`${styles.alignRight} ${styles.textRed}`}>₹{token.sellAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                        <td className={styles.alignRight}>{token.netQty?.toFixed(4) || '0.0000'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className={styles.alignCenter}>No data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dollar-wise Report */}
          <div className={styles.reportSection}>
            <div className={`${styles.reportHeader} ${styles.headerGreen}`}>
              <h3 className={styles.reportTitle}>💵 DOLLAR-WISE REPORT</h3>
            </div>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thGreen}>ACCOUNT</th>
                    <th className={`${styles.alignRight} ${styles.thGreen}`}>IN ($)</th>
                    <th className={`${styles.alignRight} ${styles.thRed}`}>OUT ($)</th>
                    <th className={`${styles.alignRight} ${styles.thBlue}`}>NET ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {dollarWiseData && dollarWiseData.length > 0 ? (
                    dollarWiseData.map((dollar, index) => (
                      <tr key={index}>
                        <td>{dollar.accountName || 'Dollar Account'}</td>
                        <td className={`${styles.alignRight} ${styles.textGreen}`}>${dollar.inAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                        <td className={`${styles.alignRight} ${styles.textRed}`}>${dollar.outAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                        <td className={styles.alignRight}>$ {dollar.netAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className={styles.alignCenter}>No data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* INR-wise Report */}
          <div className={styles.reportSection}>
            <div className={`${styles.reportHeader} ${styles.headerOrange}`}>
              <h3 className={styles.reportTitle}>💹 INR-WISE REPORT</h3>
            </div>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thOrange}>ACCOUNT</th>
                    <th className={`${styles.alignRight} ${styles.thGreen}`}>IN (INR)</th>
                    <th className={`${styles.alignRight} ${styles.thRed}`}>OUT (INR)</th>
                    <th className={`${styles.alignRight} ${styles.thBlue}`}>NET (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  {inrWiseData && inrWiseData.length > 0 ? (
                    inrWiseData.map((inr, index) => (
                      <tr key={index}>
                        <td>{inr.accountName || 'INR Account'}</td>
                        <td className={`${styles.alignRight} ${styles.textGreen}`}>₹{inr.inAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                        <td className={`${styles.alignRight} ${styles.textRed}`}>₹{inr.outAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                        <td className={styles.alignRight}>₹{inr.netAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className={styles.alignCenter}>No data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Balance Detail Modal */}
      {selectedItem && (
        <BalanceDetailModal
          isOpen={showBalanceDetail}
          onClose={() => setShowBalanceDetail(false)}
          refId={selectedItem.refId}
          refType={selectedItem.refType}
          title={selectedItem.title}
          displayName={selectedItem.displayName}
        />
      )}
    </div>
  );
};

export default Reports;
